import React, {
  Fragment,
  useState,
  useCallback,
  useMemo,
  useEffect,
} from 'react';
import { useRouter } from 'found';
import { truncate } from 'lodash';
import strip from 'strip';
import { Grid, CardHeader, NoSsr } from '@mui/material';
import screenSize, { SIZE } from 'ripple/Utils/screenSize';
import Main from 'ripple/Containers/Main';
import EventList from 'ripple/Events/Containers/ListViews/Events';
import tracker from 'lib/tracker';
import useIsMobile from 'lib/useIsMobile';
import classes from 'ripple/Events/index.scss';
import { useApolloClient, useMutation } from '@apollo/client';
import EventMinCard from './EventMinCard';
import { GET_PROFILE_INFO } from '../../schema/apolloQueries/commonQueries';
import { LIKED_UNLIKED_SPONSOR } from '../../schema/apolloMutations/eventDeatilsMutations';
import { useAudienceTrackerContext } from '../../lib/trackers/Audience';

export const GRID_COLS = {
  [SIZE.XS]: 2,
  [SIZE.SM]: 3,
  [SIZE.MD]: 4,
  [SIZE.LG]: 5,
  [SIZE.XL]: 6,
};

const DiscoverEventList = () => {
  const { trackView } = useAudienceTrackerContext();
  const {
    match: {
      location: { pathname },
    },
  } = useRouter();
  const size = screenSize();
  const [columns, setCols] = useState(0);

  useEffect(() => {
    setCols(GRID_COLS[size]);
  }, [size]);
  const mobile = useIsMobile();
  const [focussedEvent, setFocussedEvent] = useState(null);
  const [focussedEventViewer, setFocussedEventViewer] = useState(null);
  const [swimlanePageSize, setSwimlanePageSize] = useState(0);
  const [focussedRects, setFocussedRects] = useState(null);
  const [debounced, setDebounced] = useState(null);
  const [likeDetails, setLikeDetails] = useState({});
  const client = useApolloClient();
  const data = client.readQuery({
    query: GET_PROFILE_INFO,
  });
  const [openEventData, setOpenEventData] = useState({});
  const [upCommingEventData, setUpCommingEventData] = useState({});
  const [currentEventData, setCurrentEventData] = useState({});
  const [passtEventData, setPastEventData] = useState({});

  const [handleLike] = useMutation(LIKED_UNLIKED_SPONSOR);

  const hoverHandler = useCallback(
    (event, eventViewer, lanePageSize, e, listName, details) => {
      setLikeDetails(details);
      if (debounced) {
        clearTimeout(debounced);
        setDebounced(null);
      }
      // e.stopPropagation();
      // const rects = e.target.getClientRects();
      const timer = setTimeout(
        () => {
          setFocussedRects(e);
          setFocussedEventViewer(eventViewer);
          setSwimlanePageSize(lanePageSize);
          setFocussedEvent(event);
          tracker.trackGoogleTagManagerEvent(
            'select_item',
            'select_item',
            { event: { ...event, listName }, ...data?.getProfile },
            true,
          );
        },
        mobile ? 0 : 200,
      );
      setDebounced(timer);
    },
    [debounced, mobile, data],
  );

  const leftHandler = useCallback(() => {
    if (debounced) {
      clearTimeout(debounced);
      setDebounced(null);
    }
    setFocussedEvent(null);
    setFocussedRects(null);
    setFocussedEventViewer(null);
  }, [debounced]);

  const metaTitle = useMemo(() => {
    return focussedEvent
      ? `${strip(focussedEvent.sponsorName)} ${strip(focussedEvent.name)}`
      : '';
  }, [focussedEvent]);

  const metaDescription = useMemo(() => {
    return focussedEvent
      ? `${
          focussedEvent.summary
            ? strip(focussedEvent.summary)
            : truncate(strip(focussedEvent.description), {
                length: 140,
                separator: ' ',
              })
        } #sponsored`
      : '';
  }, [focussedEvent]);

  const heroAssetUrl = useMemo(
    () => (focussedEvent?.asset?.url ? focussedEvent?.asset?.url : ''),
    [focussedEvent],
  );

  const meta = useMemo(
    () => [
      {
        property: 'og:type',
        content: 'article',
      },
      {
        property: 'og:tag',
        content: 'sponsored',
      },
      {
        property: 'og:title',
        content: metaTitle,
      },
      {
        property: 'og:description',
        content: metaDescription,
      },
      heroAssetUrl
        ? {
            property: 'og:image',
            content: heroAssetUrl,
          }
        : {},
      {
        property: 'twitter:card',
        content: 'summary_large_image',
      },
      {
        property: 'twitter:title',
        content: metaTitle,
      },
      {
        property: 'twitter:description',
        content: metaDescription,
      },
      heroAssetUrl
        ? {
            property: 'twitter:image',
            content: heroAssetUrl,
          }
        : {},
    ],
    [heroAssetUrl, metaDescription, metaTitle],
  );

  useEffect(() => {
    tracker.trackGoogleTagManagerEvent(
      'view_item_list',
      'view_item_list',
      { ...data?.getProfile },
      true,
    );
    trackView('discover_events', {
        page_name: 'Discover Events',
        page_url: pathname,
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  let getMiniCardData;

  const setMiniCardData = eventVal => {
    if (likeDetails?.eventType === 'OPENEVENTS') {
      setOpenEventData(eventVal);
    } else if (likeDetails?.eventType === 'UPCOMINGEVENTS') {
      setUpCommingEventData(eventVal);
    } else if (likeDetails?.eventType === 'CURRENTEVENTS') {
      setCurrentEventData(eventVal);
    } else if (likeDetails?.eventType === 'PASTEVENTS') {
      setPastEventData(eventVal);
    }
  };

  if (likeDetails?.eventType === 'OPENEVENTS') {
    getMiniCardData = openEventData;
  } else if (likeDetails?.eventType === 'UPCOMINGEVENTS') {
    getMiniCardData = upCommingEventData;
  } else if (likeDetails?.eventType === 'CURRENTEVENTS') {
    getMiniCardData = currentEventData;
  } else if (likeDetails?.eventType === 'PASTEVENTS') {
    getMiniCardData = passtEventData;
  }

  // console.log('focussedEvent___', likeDetails);
  // if(focussedEvent.)
  return (
    <Main meta={meta} title='Discover Events'>
      <NoSsr>
        <Grid container spacing={3} classes={{ root: classes.root }}>
          <Grid item xs={8}>
            <CardHeader
              title='Events'
              classes={{ root: classes.headerRoot, title: classes.headerTitle }}
            />
          </Grid>
        </Grid>
        {useMemo(() => {
          return (
            <Fragment>
              <EventList
                vertical
                flexy={false}
                eventType='OPENEVENTS'
                eventsPageSize={50}
                id='applicableEvents'
                title='Applications Now Open'
                viewer={data?.getProfile}
                hoverHandler={hoverHandler}
                leftHandler={leftHandler}
                setEventData={setOpenEventData}
                eventData={openEventData}
              />
              {columns > 0 && (
                <>
                  <EventList
                    vertical={false}
                    eventType='UPCOMINGEVENTS'
                    eventsPageSize={columns}
                    id='comingsoonEvents'
                    title='Coming Soon'
                    viewer={data?.getProfile}
                    hoverHandler={hoverHandler}
                    leftHandler={leftHandler}
                    setEventData={setUpCommingEventData}
                    eventData={upCommingEventData}
                  />
                  <EventList
                    vertical={false}
                    eventType='CURRENTEVENTS'
                    eventsPageSize={columns}
                    id='partyingEvents'
                    title='Now Experiencing'
                    viewer={data?.getProfile}
                    hoverHandler={hoverHandler}
                    leftHandler={leftHandler}
                    setEventData={setCurrentEventData}
                    eventData={currentEventData}
                  />
                  <EventList
                    vertical={false}
                    eventType='PASTEVENTS'
                    eventsPageSize={columns}
                    id='pastEvents'
                    title='Past Events'
                    viewer={data?.getProfile}
                    hoverHandler={hoverHandler}
                    leftHandler={leftHandler}
                    setEventData={setPastEventData}
                    eventData={passtEventData}
                  />
                </>
              )}
              {focussedEvent
                && focussedRects
                // focussedEventViewer &&
                && swimlanePageSize && (
                  <EventMinCard
                    focussedEvent={focussedEvent}
                    focussedRects={focussedRects}
                    leftHandler={leftHandler}
                    viewer={focussedEventViewer}
                    pageSize={swimlanePageSize}
                    handleLike={handleLike}
                    eventData={getMiniCardData}
                    setEventData={setMiniCardData}
                  />
                )}
            </Fragment>
          );
        }, [
          data,
          hoverHandler,
          leftHandler,
          focussedEvent,
          focussedRects,
          focussedEventViewer,
          swimlanePageSize,
          handleLike,
          setPastEventData,
          passtEventData,
          currentEventData,
          setCurrentEventData,
          upCommingEventData,
          setUpCommingEventData,
          setOpenEventData,
          openEventData,
          getMiniCardData,
          setMiniCardData,
        ])}
      </NoSsr>
    </Main>
  );
};

export default DiscoverEventList;
